/* =====================================================================
 * @auw/permissions — pure policy evaluation.
 *
 * `isPermissionGranted` is the only function every consumer should
 * call. It is intentionally side-effect free so callers can invoke it
 * inside `useMemo`, reducers, or server-side prerender paths.
 * ===================================================================== */

import type {
  Permission,
  PermissionContext,
  RoleDefinition,
} from './types';

/** Look up a role definition by id. Returns `undefined` when the
 *  role isn't part of the current tenant manifest. */
export function getRoleById(
  roles: readonly RoleDefinition[],
  id: string | null | undefined,
): RoleDefinition | undefined {
  if (!id) return undefined;
  for (const role of roles) {
    if (role.id === id) return role;
  }
  return undefined;
}

/** Decide whether the active role grants `permission`.
 *
 *  Rules:
 *    - No role → denied.
 *    - `policy.grants === '*'` → granted for every permission.
 *    - Explicit list → granted iff the permission is present.
 */
export function isPermissionGranted(
  ctx: PermissionContext,
  permission: Permission,
): boolean {
  const role = getRoleById(ctx.roles, ctx.userRoleId);
  if (!role) return false;

  const grants = role.policy.grants;
  if (grants === '*') return true;

  for (const granted of grants) {
    if (granted === permission) return true;
  }
  return false;
}

/** Bulk checker — useful when building memoised capability bags like
 *  `const { canManageSkills, canRefreshFeed } = usePermissions(...);` */
export function collectGrants(
  ctx: PermissionContext,
  permissions: readonly Permission[],
): Record<string, boolean> {
  const role = getRoleById(ctx.roles, ctx.userRoleId);
  if (!role) {
    const out: Record<string, boolean> = {};
    for (const p of permissions) out[p] = false;
    return out;
  }

  const grants = role.policy.grants;
  if (grants === '*') {
    const out: Record<string, boolean> = {};
    for (const p of permissions) out[p] = true;
    return out;
  }

  const grantedSet = new Set<string>(grants);
  const out: Record<string, boolean> = {};
  for (const p of permissions) out[p] = grantedSet.has(p);
  return out;
}
